<script setup lang="ts">
import {
  hexAlpha,
  mixColor,
  roundProgressPercent,
  xpPairLabel,
} from '@/utils/levels';

// Faixa de nível do header do jogo (Figma 22526:60672). Ocupa a LARGURA TODA
// da barra, numa linha só pra ela: [emblema] · [Elo: lv N] · [placa].
//
// A placa (nó "Union", 22631:60039) é uma chapa #1B1F27 de dois degraus: em
// cima, a faixa inteira com a barra de XP e a porcentagem; embaixo, um degrau
// mais curto, fechado por um chanfro diagonal. Ela não é uma imagem: é a união
// de dois retângulos arredondados mais o triângulo do chanfro, e sai em CSS
// porque o degrau de baixo tem que MEDIR O PRÓPRIO CONTEÚDO — uma chapa
// exportada com a largura do desenho ficaria sobrando no ar.
//
// É esse "medir o conteúdo" que dá conta das quatro variantes do Figma
// (22624:72182 e 22802:60807/60889/60971) sem nenhum caso especial de forma —
// elas só trocam QUEM mora no degrau:
//
//   • com benefício  → os chips no degrau, e o contador de XP à direita, FORA
//                      da chapa (apagado, pra não disputar com os chips);
//   • sem benefício  → o contador de XP DENTRO da chapa, branco chapado, e o
//                      degrau encolhe até o tamanho dele.
//
// NÃO é clicável: a faixa mora a um dedo do topo do jogo, e um toque errado
// tirava o jogador da partida sem querer. Quem quiser ver o progresso todo tem
// o "Meu nível" na bandeja do header. Por isso é uma <div> com role de imagem,
// e não um <button>.
const {
  tier,
  level,
  xp,
  levelXp,
  levelXpSpan,
  isMaxLevel,
  levelProgress,
  currentLevelLabel,
} = useLevelsProgress();

// Os benefícios saem das `core_public_tags` do perfil da Smartico. Lista vazia
// não prova que o jogador não tem nada — até o `identify` concluir, ninguém
// tem; por isso os chips só APARECEM quando o dado chega, e nada na faixa
// depende deles pra existir.
const { activeBenefits, hasAnyBenefit } = useLevelBenefits();

const progressLabel = computed(() => roundProgressPercent(levelProgress.value));

// Contador de XP (22799:60743), medido DENTRO do nível atual: o que o jogador
// andou desde a entrada dele contra o que o nível custa. É a mesma régua da
// barra ao lado — o acumulado da vida contra o teto absoluto do próximo nível
// daria um par que não conversa com a porcentagem.
//
// No topo da escala não há "próximo" pra dividir, e aí sai só o acumulado.
// `isMaxLevel` só afirma o topo quando a régua da Smartico confirma, então não
// corremos o risco de esconder o teto de quem está no meio do caminho.
const xpLabel = computed(() =>
  isMaxLevel.value
    ? xpPairLabel(xp.value)
    : xpPairLabel(levelXp.value, levelXpSpan.value),
);
</script>

<template>
  <!-- SEM `relative` no root: quem monta posiciona (o `.relative` do Tailwind
       vence o `.absolute` do pai com important:true — já nos mordeu). -->
  <div
    :aria-label="currentLevelLabel"
    class="levels-ingame flex w-full items-center gap-[5px]"
    role="img"
  >
    <!-- Emblema em tamanho cheio: antes era um fundo de 52px atravessando a
         borda da faixa e clipado em três lados pra não vazar, o que comia a
         largura do texto e cortava o brasão. -->
    <span
      aria-hidden="true"
      class="h-[19.01px] w-[21.76px] flex-none bg-contain bg-center bg-no-repeat"
      :style="{
        backgroundImage: `url(${tier.badge})`,
        filter: 'drop-shadow(0 1.4px 4.9px rgba(0,0,0,.55))',
      }"
    ></span>

    <!-- Nome do elo em negrito + "lv N" apagado, do jeito que o Figma escreve
         (era "· nível N"). `shrink-0` porque quem cede espaço é a barra. -->
    <span
      class="shrink-0 whitespace-nowrap text-[11.222px] font-semibold leading-none text-white/60"
    >
      <b class="font-bold text-white">{{ tier.name }}:</b>
      lv {{ level }}
    </span>

    <span class="plate">
      <!-- Degrau de cima: atravessa a placa inteira. A quina de baixo à
           esquerda fica RETA porque é onde o degrau dos chips continua — se
           arredondasse, abriria um entalhe no meio da chapa. -->
      <span
        aria-hidden="true"
        class="plate__top"
      ></span>

      <span class="plate__bar">
        <span
          class="track"
          :style="{ borderColor: hexAlpha(tier.color, 0.4) }"
        >
          <span
            class="track__fill"
            :style="{
              width: `${levelProgress}%`,
              background: `linear-gradient(90deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.42)})`,
              boxShadow: `0 0 9.2px ${hexAlpha(tier.color, 0.9)}`,
            }"
          ></span>
        </span>

        <span class="percent">{{ progressLabel }}%</span>
      </span>

      <!-- Segundo degrau: a chapa (que mede o conteúdo) e, quando há chips, o
           contador de XP ao lado dela, encostado na direita da placa. -->
      <span class="plate__stepRow">
        <span class="plate__step">
          <template v-if="hasAnyBenefit">
            <LevelsBenefitChip
              v-for="benefit in activeBenefits"
              :key="benefit.id"
              :benefit="benefit"
            />
          </template>

          <span
            v-else
            class="xp xp--inside"
            >{{ xpLabel }}</span
          >

          <!-- O chanfro. Era um triângulo de `clip-path`, e é justamente aí que
               a emenda com a chapa aparecia: o corte reto encontrava o cinza em
               quina viva, enquanto o desenho faz a diagonal NASCER de uma
               curva. Este path é a geometria do próprio "Union" (22631:60039)
               recortada e reescalada pra altura do nosso degrau — os dois
               arredondamentos (o de cima, que funde com a faixa, e o de baixo,
               que fecha no degrau) são os mesmos do Figma.

               Vai 1px acima do degrau (`viewBox` começa em -1) só pra cobrir a
               emenda: as duas peças são da mesma cor, e sem a sobreposição um
               arredondamento de subpixel deixa passar um fio do jogo entre
               elas. -->
          <svg
            aria-hidden="true"
            class="plate__chamfer"
            viewBox="0 -1 22.78 20.5"
          >
            <path
              d="M0-1H22.78V0C20.56 0 18.46 1 17.07 2.72L5.71 16.77C4.32 18.5 2.22 19.5 0 19.5Z"
              fill="currentColor"
            />
          </svg>
        </span>

        <span
          v-if="hasAnyBenefit"
          class="xp"
          >{{ xpLabel }}</span
        >
      </span>
    </span>
  </div>
</template>

<style scoped lang="scss">
// Cor da chapa (Figma: fill do "Union"). Fica numa variável porque os três
// pedaços da placa — degrau de cima, degrau dos chips e chanfro — têm que ser
// exatamente a mesma cor, senão a emenda aparece.
$plate-bg: #1b1f27;
$plate-radius: 5px;
// Alturas dos dois degraus. Em cima, os 14,593 do desenho. Embaixo, o degrau
// dos chips saiu dos 13,42 do Figma pra 19,5: com a medida do desenho o chip
// (12,693) ocupava o degrau inteiro e encostava nas duas quinas do cinza, como
// se estivesse vazando a placa. Agora sobram ~3,4 de cada lado — o chip mora
// DENTRO da chapa, e não em cima dela, e a chapa desce um pouco mais.
$plate-top-h: 14.593px;
$plate-step-h: 19.5px;
// Chanfro: os 15,68 × 13,42 do desenho reescalados pelo mesmo fator do degrau
// (19,5 / 13,42 = 1,453), pra a diagonal manter a INCLINAÇÃO do Figma agora que
// o degrau é mais alto. Esticar só a largura deitaria a diagonal; encolher só a
// altura a deixaria a prumo.
$plate-chamfer-w: 22.78px;

.plate {
  @apply relative block min-w-0 flex-1;

  height: $plate-top-h + $plate-step-h;
  // Rede de segurança: a fileira de chips tem largura de CONTEÚDO e não sabe
  // onde a placa acaba. Hoje ela nunca chega lá (são três pílulas contra ~278
  // de placa, e o teto real é o tamanho do catálogo, não o número de tags que o
  // core manda), mas o dia que alguém acrescentar benefício demais o estrago
  // fica DENTRO da placa em vez de vazar por cima do jogo.
  // `clip` e não `hidden`: `hidden` faz da placa um container de rolagem, e um
  // chip cortado poderia ser "rolado" pra dentro da vista pelo navegador —
  // exatamente o fantasma que já nos pegou nas bordas da roletinha.
  overflow: clip;
}

.plate__top {
  @apply absolute inset-x-0 top-0 block;

  height: $plate-top-h;
  border-radius: $plate-radius $plate-radius $plate-radius 0;
  background: $plate-bg;
}

// Barra + porcentagem, na altura do degrau de cima.
.plate__bar {
  @apply relative flex items-center;

  height: $plate-top-h;
  // 2 a mais que os 3,69 do desenho à esquerda: encostados na quina, o trilho e
  // o chip logo abaixo dele pareciam vazar a placa.
  padding: 0 3.69px 0 5.69px;
  gap: 6px;
}

// Trilho de 7px com traço na cor do elo a 40% e o fundo azul-escuro do
// desenho. A sombra interna é o que dá a impressão de sulco.
.track {
  @apply block min-w-0 flex-1 overflow-hidden;

  height: 7px;
  border-width: 0.632px;
  border-style: solid;
  border-radius: 999px;
  background: #001965;
  box-shadow: inset 0 1.153px 2.306px rgba(0, 0, 0, 0.5);
}

.track__fill {
  @apply block h-full rounded-full;
}

.percent {
  @apply shrink-0 tabular-nums;

  color: #fff;
  font-size: 9.597px;
  font-weight: 700;
  line-height: 1;
}

// A linha do segundo degrau: a chapa à esquerda e o contador de XP no que
// sobra. `items-start` porque a chapa manda na altura — o contador não pode
// esticá-la.
.plate__stepRow {
  @apply flex items-start;
}

// Degrau de baixo: largura do CONTEÚDO (`w-fit`), porque é ele que veste o que
// o jogador realmente tem — os chips, ou o próprio contador quando não há
// benefício nenhum.
.plate__step {
  @apply relative flex w-fit items-center;

  height: $plate-step-h;
  // Mesmos 2px a mais da faixa de cima, pra o primeiro chip ficar a prumo com o
  // começo do trilho.
  padding: 0 3.02px 0 5.02px;
  gap: 4.022px;
  // Arredondado só embaixo à ESQUERDA: à direita quem fecha a chapa é o
  // chanfro, e era essa quina arredondada sobrando debaixo dele que abria o
  // entalhe entre os dois.
  border-radius: 0 0 0 $plate-radius;
  background: $plate-bg;
  // Cor do chanfro, que a herda por `currentColor` — assim a emenda não tem como
  // sair de tom.
  color: $plate-bg;
}

.plate__chamfer {
  @apply absolute left-full block;

  top: -1px;
  width: $plate-chamfer-w;
  height: $plate-step-h + 1px;
}

// Contador de XP. Fora da chapa ele come o que sobra da placa e cola na
// direita; `min-w-0` + reticências são a rede pra tela estreita — com três
// chips num 360 a sobra é da ordem da largura do texto, e é melhor perder o
// primeiro dígito do que empurrar a placa.
// `relative` porque o chanfro avança ~23px pra dentro do espaço do contador:
// sem contexto próprio, ele passaria por cima do texto.
// `flex` + `items-center` e NÃO `line-height` igual à altura: com line-height o
// texto se pendura na entrelinha da fonte (que tem ascendente e descendente
// diferentes) e sai sempre um fio abaixo do centro do degrau. Centrado por
// flexbox, quem manda é a caixa.
.xp {
  @apply relative flex min-w-0 flex-1 items-center justify-end overflow-hidden tabular-nums;

  height: $plate-step-h;
  padding-right: 3.69px;
  // Apagado quando mora FORA da chapa: aí ele está por cima do preto da barra,
  // ao lado dos chips, e o desenho não quer que dispute com eles.
  color: rgba(255, 255, 255, 0.5);
  font-size: 8.03px;
  font-style: italic;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
}

// Dentro da chapa (jogador sem benefício): mede o próprio texto — é ele que dá
// a largura do degrau — e sai branco chapado, porque aí ele é o único conteúdo
// do degrau e tem o cinza da placa atrás.
.xp--inside {
  @apply flex-none;

  padding-right: 0;
  color: #fff;
}
</style>
